import React from 'react';

import { AppState, Auth0Provider } from '@auth0/auth0-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConnectedRouter } from 'connected-react-router';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';

import { AuthProvider } from 'auth/AuthContext';
import LDProvider from 'domains/core/components/LDProvider';
import useConfig, { ConfigProvider } from 'hooks/useConfig';

import App from './App';
import store, { history } from './state';

const queryClient = new QueryClient();

const AppUsingConfig = () => {
    const { config, loading } = useConfig();

    if (loading) {
        return null;
    }

    const onRedirectCallback = (appState: AppState) => history.push(appState?.returnTo || window.location.pathname);

    return (
        <Provider store={store}>
            <ConnectedRouter history={history}>
                <Auth0Provider
                    domain={config.AUTH0_DOMAIN}
                    clientId={config.AUTH0_CLIENT_ID}
                    redirectUri={config.AUTH0_REDIRECT_URI}
                    audience={config.AUTH0_AUDIENCE}
                    useRefreshTokens={true}
                    cacheLocation="localstorage"
                    onRedirectCallback={onRedirectCallback}
                >
                    <AuthProvider>
                        <QueryClientProvider client={queryClient}>
                            <LDProvider>
                                <App />
                            </LDProvider>
                        </QueryClientProvider>
                    </AuthProvider>
                </Auth0Provider>
            </ConnectedRouter>
        </Provider>
    );
};

const AppWithProviders = () => (
    <React.StrictMode>
        <ConfigProvider>
            <AppUsingConfig />
        </ConfigProvider>
    </React.StrictMode>
);

if (process.env.NODE_ENV !== 'production') {
    const axe = require('@axe-core/react');
    axe(React, ReactDOM, 1000);
}

ReactDOM.render(<AppWithProviders />, document.getElementById('root'));
